import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from 'state/axios';
import { addMonths, differenceInMinutes } from "date-fns";
import toast from 'react-hot-toast';
import { 
  useMediaQuery, 
  useTheme, 
  Box, 
  Typography, 
  CircularProgress, 
  Tooltip, 
  MenuItem, 
  Select, 
  Chip,
} from '@mui/material';
import {
  Update,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { setFlights, appendFlights } from "state";
import { LAYOUT_HEIGHT, EXCLUDED_FLIGHT_STATUS } from 'state/constants';
import { chunkFlights } from 'state/api';
import { 
  removeItemByValueImmutable,     
  filterFlights,
  formatTZDate,
  parseUTCDate,
} from 'state/helpers';
import { hasPermission } from 'state/checkPermission';
import ScrollToTop from 'components/ScrollToTop';
import NumberControl from 'components/NumberControl';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import DataGridComponent from 'components/DataGridComponent';
import FlightFilterDialog from 'components/FlightFilterDialog';
import CrewListDialog from 'components/CrewListDialog';
import DispatcherChecklist from './DispatcherChecklist';


const DEPARTURE_THRESHOLDS = [
  { max: 0, level: "departed" },
  { max: 60, level: "critical" },
  { max: 180, level: "warning-high" },
  { max: 360, level: "warning" },
];

const getDepartureLevel = (minutes) => {
  if (minutes == null) return "normal";
  for (const t of DEPARTURE_THRESHOLDS) {
    if (minutes <= t.max) return t.level;
  }
  return "normal";
};

// Module-level cache: same color → same object reference → emotion CSS cache hit during scroll
const _pulseCache = {};
const getPulse = (color) => {
  if (!_pulseCache[color]) {
    _pulseCache[color] = {
      animation: "dispatcherPulse 2s ease-in-out infinite",
      "@keyframes dispatcherPulse": {
        "0%":   { boxShadow: `0 0 0 0 ${color}` },
        "50%":  { boxShadow: `0 0 0 4px rgba(0,0,0,0)` },
        "100%": { boxShadow: `0 0 0 0 rgba(0,0,0,0)` },
      },
    };
  }
  return _pulseCache[color];
};

const buildKey = (obj) => {
  if (!obj) return "";
  const dateStr = formatTZDate(obj.flightDate, 'UTC', 'yyyy-MM-dd');
  return `${dateStr}_${obj.flightDesc}_${obj.dep}`;
};


const DispatcherChecklists = () => {
  useAuthCheck();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user); 
  const timeMode = useSelector((state) => state.timemode);
  const flightsData = useSelector((state) => state.flights);
  const params = useSelector((state) => state.params);
  const theme = useTheme(); 
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const paramsMap = useMemo(
    () => new Map(params?.map(p => [p.param, p.value]) ?? []),
    [params]
  );
  const LOAD_CHUNK_DAYS = Number(paramsMap.get("LOAD_CHUNK_DAYS")) || 31;
  const FETCH_TYPE = paramsMap.get("FETCH_TYPE") || "SEQUENCE"; 

  const canRefresh = hasPermission(user?.permissions, "DispatcherChecklists", "refresh", false);

  const { startDate, endDate } = useDateRangeContext();  
  const scrollRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [offSet, setOffSet] = useState(0);  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(null);
  const [dialogState, setDialogState] = useState({
    openCrewList: false,
    flight: [],
    crewList: [],
    jumpList: [],
  });

  const [processedFlights, setProcessedFlights] = useState([]);
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistMap, setChecklistMap] = useState({});
  
    
  const fetchChecklists = useCallback(async () => {
    try {
      const res = await api.get(
        '/dispatcherchecklists',
        {
          params: {
            startDate: parseUTCDate(startDate),
            endDate: parseUTCDate(endDate),
          },
        }
      );

      const { checklistItems, dispatcherChecklists } = res.data;

      setChecklistItems(checklistItems);

      const map = {};
      dispatcherChecklists.forEach((c) => {
        const key = buildKey(c);
        map[key] = c;
      });
      
      setChecklistMap(map);
    } catch (error) {
      toast.error(error?.message || "Failed to load checklist!");
    }
  }, [startDate, endDate]);
  
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 300000); 

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchChecklists();
    }
  }, [startDate, endDate, fetchChecklists]);

  const handleUpdateStatus = useCallback(
    async (row, itemId, status) => {
      try {
        const { data } = await api.put(
          '/dispatcherchecklists/update/status',
          {
            flightDate: row.flightDate,
            flightDesc: row.flightDesc,
            dep: row.dep,
            itemId,
            status,
          }
        );

        const updatedChecklist = data.checklist;

        const key = buildKey(row);
        setChecklistMap(prev => ({
          ...prev,
          [key]: {
            ...updatedChecklist,
            actions: updatedChecklist.actions || [],
          }
        }));

      } catch (error) {
        toast.error(error?.message || "Failed to update checklist status!");
      }
    },
    []
  );

  useEffect(() => {        
    const processFlights = () => {
      try {      
        setError(null);     
        setLoading(true);
        
        const flights = removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS);
        setProcessedFlights(flights);
      } catch (error) {
        setError(error);
        toast.error(error?.message || "Failed to process flights!");
      } finally {
        setLoading(false);
      }
    };

    processFlights();        
  }, [timeMode, flightsData]);
  
  const sortedFlights = useMemo(() => {
    const filtered = filterFlights({ flights: processedFlights, filters, timeMode });
    return filtered.map((f) => {
      const key = buildKey(f);
      const checklist = checklistMap[key];
      return {
        ...f,
        id: f._id || key,
        checklist: checklist || null,
        overallStatus: checklist?.overallStatus || "pending",
      };
    });
  }, [processedFlights, filters, timeMode, checklistMap]);

  const getMonthLabel = (q) => 
    q < -1 ? "Previous months" :
    q === -1 ? "Previous month" :
    q === 0 ? "Current month" :
    q === 1 ? "Next month" :
    q > 1 ? "Next months" : "";

  const label = getMonthLabel(offSet);

  const levelConfig = useMemo(() => ({
    warning: { color: theme.palette.warning.main },
    "warning-high": { color: theme.palette.warning.dark },
    critical: { color: theme.palette.error.main },
  }), [theme]);

  const handleOffSetChange = (newOffSet) => {
    setOffSet(newOffSet); 
  };

  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);
  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };

  const handleCloseCrewListDialog = () => {
    setDialogState(prev => ({ ...prev, openCrewList: false }));
  };    

  const handleFlights = useCallback(async () => {
    if (!!startDate || !endDate) return null;

    try {
      setError(null);
      setLoading(true);
      setProgress(0);
      
      const prev = Math.min(offSet, 0);
      const next = Math.max(offSet, 0);    
      const fromDate = addMonths(startDate, prev);
      const toDate = addMonths(endDate, next);    

      const controller = new AbortController();
      let isFirstChunk = true;
      await chunkFlights(
        timeMode,
        fromDate,
        toDate,
        LOAD_CHUNK_DAYS,
        FETCH_TYPE,
        {
          concurrency: 2,
          retries: 2,
          backoff: 1000,
          signal: controller.signal,
          onProgress: (p) => setProgress(p),
          onChunk: (chunk) => {
            if (isFirstChunk) {
              dispatch(setFlights({ flights: chunk }));
              isFirstChunk = false;
            } else {
              dispatch(appendFlights({ flights: chunk }));
            }
          },
        }
      );

      toast.success("Refresh flights successfully.");
    } catch (error) {
      setError(error);
      toast.error(error?.message || "Failed to load flights!");
    } finally {
      setLoading(false);
    }
  }, [
    dispatch, 
    timeMode, 
    startDate, 
    endDate, 
    offSet,
    LOAD_CHUNK_DAYS,
    FETCH_TYPE,
  ]);
  
  const {
    acTypeOptions,
    flightRegOptions,
    sectorTypeOptions,
    departureTypeOptions,
    arrivalTypeOptions,
  } = useMemo(() => {
    if (!Array.isArray(processedFlights)) {
      return {
        acTypeOptions: [],
        flightRegOptions: [],
        sectorTypeOptions: [],
        departureTypeOptions: [],
        arrivalTypeOptions: [],
      };
    }

    const sorted = (key) =>
      [...new Set(processedFlights.map((f) => f[key]).filter(Boolean))].sort();

    return {
      acTypeOptions: sorted("acType"),
      flightRegOptions: sorted("flightReg"),
      sectorTypeOptions: sorted("sectorType"),
      departureTypeOptions: sorted("departureType"),
      arrivalTypeOptions: sorted("arrivalType"),
    };
  }, [processedFlights]);   
  
  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      ...isMobile ? { width: 60 } : { flex: 1 },
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: timeMode === 'UTC' ? "flightDate" : "flightDateLocalBase",
      headerName: "FlightDate",
      ...isMobile ? { width: 100 } : { flex: 1.5 },
      valueFormatter: (value) => {       
        return `${formatTZDate(value, 'UTC', 'dd/MM/yy')}`;
      },
    },      
    {
      field: "flightDesc",
      headerName: "FlightDesc",
      ...isMobile ? { width: 80 } : { flex: 1.2 },
    },  
    {
      field: "sector",
      headerName: "Sector",
      align: "center",
      ...isMobile ? { width: 100 } : { flex: 1.6 },
    },
    {
      field: "acType",
      headerName: "ACType",
      ...isMobile ? { width: 80 } : { flex: 1.2 },
    },
    {
      field: "flightReg",
      headerName: "Reg",
      ...isMobile ? { width: 100 } : { flex: 1.5 },
    },      
    {
      field: timeMode === 'UTC' ? "etd" : "etdLocalBase",
      headerName: "Etd",
      align: "center",
      ...isMobile ? { width: 80 } : { flex: 1.2 },
    },
    {
      field: timeMode === 'UTC' ? "eta" : "etaLocalBase",
      headerName: "Eta",
      align: "center",
      ...isMobile ? { width: 80 } : { flex: 1.2 },
    },      
    {
      field: "blockHours",
      headerName: "BH",
      ...(isMobile ? { width: 80 } : { flex: 1.2 }),
      type: "number",
      align: "right",
      headerAlign: "left",
      valueFormatter: (params) => {
        return params > 0 ? Number(params).toFixed(2) : 0;
      },
    },
    {
      field: "overallStatus",
      headerName: "Status",
      ...isMobile ? { width: 100 } : { flex: 1.6 },
      renderCell: ({ row }) => {
        const diffMinutes = row.fullEtd
          ? differenceInMinutes(new Date(row.fullEtd), now)
          : null;
        const level = getDepartureLevel(diffMinutes);
        const config = levelConfig[level];
        const isPending = row.overallStatus !== "done";
        const shouldPulse = isPending && level !== "departed" && config;

        const color =
          row.overallStatus === "done" ? "success" :
          level === "critical" ? "error" :
          level === "warning-high" || level === "warning" ? "warning" : "default";

        return (
          <Box
            sx={{
              height: "100%",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              ...(shouldPulse ? getPulse(config.color) : {}),
            }}
          >
            <Chip size="small" label={row.overallStatus} color={color} />
            {diffMinutes > 0 && (
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  fontWeight: 600,
                  color:
                    level === "critical" ? "error.main" :
                    level === "warning-high" ? "warning.dark" : "text.secondary",
                }}
              >
                ⏳ {Math.floor(diffMinutes / 60)}h {diffMinutes % 60}m
              </Typography>
            )}
          </Box>
        );
      },
    },
  ], [timeMode, isMobile, levelConfig, now]);
  
  const checklistColumns = useMemo(() => {
    return checklistItems.map((item) => ({
      field: `check_${item.code}`,
      headerName: item.code,
      filterable: false,      
      sortable: false,
      ...isMobile ? { width: 120 } : { flex: 2.2 },
      renderCell: (params) => {
        const checklist = params.row.checklist;
        
        const action = checklist?.actions?.find((a) => {
          const actionItemId = typeof a.item === 'object' ? a.item._id : a.item;
          return String(actionItemId) === String(item._id);
        });

        const value = action?.currentStatus || "pending";

        return (
          <Select
            fullWidth
            size="small"
            value={value}
            onChange={(e) => handleUpdateStatus(params.row, item._id, e.target.value)}
            sx={{ 
              color: value === 'rejected' ? 'error.main' : 
                value === 'warning' ? 'warning.main' : 
                value === 'checked' ? 'success.main' : 'inherit',
            }}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="checked">Checked</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        );
      },
    }));
  }, [isMobile, checklistItems, handleUpdateStatus]);
  
  const hiddenColumns = useMemo(() => {
    return {
      _id: false,      
    }
  }, []);

  const nonMobileHiddenColumns = useMemo(() => {
    return {
      _id: false,      
    }
  }, []);
      
  const columnsToShow = [
    ...columns,
    ...checklistColumns,
  ];

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    isMobile ? hiddenColumns : nonMobileHiddenColumns
  );

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
  }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);

    
  return (
    <Box className="main-container-box">                 
      <FlexBetween>
        <Header 
          title="Dispatcher Checklist" 
          subtitle={loading ? "Loading..." : `List of ${sortedFlights?.length || 0} Flights`}
        />
        {!loading && (                    
          <Box display="flex" alignItems="center" gap={1}>  
            {!isMobile && canRefresh && (     
              <Box display="flex" alignItems="center" gap={1}>  
                <Tooltip title={label} arrow>
                  <span>
                    <NumberControl
                      min={-23}
                      max={23}
                      value={offSet}
                      onChange={handleOffSetChange} 
                      textFieldWidth={45}
                    />
                  </span>
                </Tooltip>
                <Tooltip title="Refresh" arrow>
                  <span>
                    <StyledButton onClick={handleFlights}>
                      <Update />
                    </StyledButton>
                  </span>              
                </Tooltip>
              </Box>
            )}  
            
            <FilterButton
              filters={filters}
              onClick={handleFilterOpen}
            />
          </Box>
        )}                
      </FlexBetween>          
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            alignContent="center" 
            gap={1}
          >
            <CircularProgress />
            <Typography>{progress || 0}%</Typography>
          </Box>
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: "auto" }}
          >
            {isMobile ? (
              <DispatcherChecklist
                rows={sortedFlights}
                checklistItems={checklistItems}
                onUpdateStatus={handleUpdateStatus}
                now={now}
              />
            ) : (
              <DataGridComponent
                rows={sortedFlights}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}
                      
      {isFilterDialogOpen && (
        <FlightFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          acTypeOptions={acTypeOptions} 
          flightRegOptions={flightRegOptions}
          sectorTypeOptions={sectorTypeOptions}
          departureTypeOptions={departureTypeOptions}
          arrivalTypeOptions={arrivalTypeOptions}
        />
      )}
      
      {dialogState.openCrewList && (
        <CrewListDialog 
          open={dialogState.openCrewList}
          onClose={handleCloseCrewListDialog}
          flight={dialogState.flight}
          crewList={dialogState.crewList}
          jumpList={dialogState.jumpList}
        />
      )}         
    </Box>
  );
};

export default DispatcherChecklists;
